import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import { PortalReadState } from '../portal-read-state.tsx';
import '../portal-page.css';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { adminSecurityApi, AdminApiError } from '../../api/admin.ts';
import { createCredential, wasDismissed, webauthnSupported } from '../../lib/webauthn.ts';
import { Alert, Button, LoadingBlock, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import AdminElevateDialog, { isAdminFreshAuthRequired } from '../../components/admin/elevate-dialog.tsx';

/**
 * The operator's own security screen.
 *
 * This page is not optional furniture, even though `mfa_require_admin` now
 * ships OFF. It is the only way to enroll the factor that executing a payout
 * batch and resetting a tenant user's factor still demand — the freshness gate
 * refuses an operator holding none, whatever the mandate says — and it is where
 * `issueAdminSession` walks every operator with nothing enrolled the moment a
 * deployment does turn the mandate on. Without the route that sign-in lands on
 * the not-found screen with no way to enroll and no way to reach the admin APIs
 * that demand a fresh factor — a console locked by its own security feature.
 *
 * No SMS. An operator who can execute a payout batch should not be one SIM
 * swap away from an attacker, which is why the server filters it out of
 * `availableMethods` for this realm and why nothing here offers it.
 */
export default function AdminSecurityPage() {
  useDocumentTitle('Security');
  const [searchParams] = useSearchParams();
  const mandated = searchParams.get('setup') === '1';

  const status = useQuery({ queryKey: ['admin-security'], queryFn: ({ signal }) => adminSecurityApi.status(signal) });
  const devices = useQuery({
    queryKey: ['admin-trusted-devices'],
    queryFn: ({ signal }) => adminSecurityApi.trustedDevices(signal),
  });
  const sessions = useQuery({
    queryKey: ['admin-sessions'],
    queryFn: ({ signal }) => adminSecurityApi.sessions(signal),
  });
  const [error, setError] = useState<string | null>(null);

  // Shown once each, so held here rather than re-fetched: there is nothing to
  // re-fetch.
  const [enrollment, setEnrollment] = useState<{ methodId: string; secret: string; uri: string } | null>(null);
  const [phone, setPhone] = useState('');
  const [smsChallenge, setSmsChallenge] = useState<{ challengeId: string; maskedPhone: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  /**
   * The action a freshness 403 interrupted, held so it can be resumed.
   *
   * Enrolling the FIRST factor stamps the session, so the setup path never
   * lands here. Everything after it does: once that five-minute stamp expires,
   * adding a second factor, removing one and regenerating recovery codes all
   * answer `FRESH_AUTH_REQUIRED`, and an ordinary MFA sign-in deliberately
   * does not clear it. Without a way through, the operator could never manage
   * their own MFA again.
   */
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const failed = (caught: unknown, fallback: string, retry?: () => void) => {
    if (isAdminFreshAuthRequired(caught)) {
      setError(null);
      setPendingAction(() => retry ?? null);
      return;
    }
    setError(caught instanceof AdminApiError ? caught.message : fallback);
  };

  const startTotp = useMutation({
    mutationFn: () => adminSecurityApi.enrollTotp(),
    onSuccess: (result) => {
      setEnrollment(result);
      setError(null);
    },
    onError: (caught) => failed(caught, 'That authenticator could not be set up.', () => startTotp.mutate()),
  });

  const finishTotp = useMutation({
    mutationFn: () => adminSecurityApi.verifyTotp(enrollment?.methodId ?? '', code),
    onSuccess: (result) => {
      setEnrollment(null);
      setCode('');
      setError(null);
      if (result.recoveryCodes) setRecoveryCodes(result.recoveryCodes);
      void status.refetch();
    },
    onError: (caught) => failed(caught, 'That code is not right.'),
  });

  const startSms = useMutation({
    mutationFn: () => adminSecurityApi.enrollSms(phone),
    onSuccess: (result) => {
      setSmsChallenge(result);
      setError(null);
    },
    onError: (caught) => failed(caught, 'That code could not be sent.', () => startSms.mutate()),
  });

  const finishSms = useMutation({
    mutationFn: () => adminSecurityApi.verifySms(smsChallenge?.challengeId ?? '', code),
    onSuccess: (result) => {
      setSmsChallenge(null);
      setPhone('');
      setCode('');
      setError(null);
      if (result.recoveryCodes) setRecoveryCodes(result.recoveryCodes);
      void status.refetch();
    },
    onError: (caught) => failed(caught, 'That code is not right.'),
  });

  const addSecurityKey = useMutation({
    mutationFn: async () => {
      const options = await adminSecurityApi.securityKeyOptions();
      const created = await createCredential(options.publicKey);
      // Dismissing the platform prompt is a choice, not a failure.
      if (!created) return null;
      return adminSecurityApi.registerSecurityKey({
        challengeId: options.challengeId,
        label: 'Security key',
        clientDataJson: created.clientDataJson,
        attestationObject: created.attestationObject,
        ...(created.transports ? { transports: created.transports } : {}),
      });
    },
    onSuccess: (result) => {
      setError(null);
      if (result?.recoveryCodes) setRecoveryCodes(result.recoveryCodes);
      void status.refetch();
    },
    onError: (caught) => {
      if (wasDismissed(caught)) return;
      failed(caught, 'That security key could not be registered.', () => addSecurityKey.mutate());
    },
  });

  const clearDevices = useMutation({
    mutationFn: () => adminSecurityApi.clearTrustedDevices(),
    onSuccess: () => void devices.refetch(),
    onError: (caught) => failed(caught, 'Those devices could not be forgotten.', () => clearDevices.mutate()),
  });

  const signOutOthers = useMutation({
    mutationFn: () => adminSecurityApi.revokeOtherSessions(),
    onSuccess: () => void sessions.refetch(),
    onError: (caught) => failed(caught, 'Those sessions could not be ended.'),
  });

  const removeMethod = useMutation({
    mutationFn: (id: string) => adminSecurityApi.removeMethod(id),
    onSuccess: () => {
      setError(null);
      void status.refetch();
    },
    onError: (caught, id) => failed(caught, 'That method could not be removed.', () => removeMethod.mutate(id)),
  });

  const regenerate = useMutation({
    mutationFn: () => adminSecurityApi.regenerateRecoveryCodes(),
    onSuccess: (result) => {
      setRecoveryCodes(result.codes);
      setError(null);
      void status.refetch();
    },
    onError: (caught) => failed(caught, 'New codes could not be generated.', () => regenerate.mutate()),
  });

  if (status.isFetching) return <PortalReadState title="Security" label="Loading security methods…" />;
  if (status.isError || !status.data)
    return (
      <PortalReadState
        title="Security"
        error={status.error instanceof Error ? status.error.message : 'Security details could not be loaded.'}
        retry={() => status.refetch()}
      />
    );
  const data = status.data;

  const needsSetup = data.required && data.methods.length === 0;

  return (
    <div className="portal-page">
      <WorkPage
        title="Security"
        description="Your own second factor for the operator console. It is what stands in front of a payout batch."
      >
        {error ? <Alert tone="error">{error}</Alert> : null}

        {/* The gate said "prove it's you first". This is the way through. */}
        {pendingAction !== null ? (
          <AdminElevateDialog
            onVerified={() => {
              const resume = pendingAction;
              setPendingAction(null);
              resume?.();
            }}
            onCancel={() => setPendingAction(null)}
          />
        ) : null}

        {needsSetup ? (
          <Alert tone="warning">
            {mandated
              ? 'This console requires two-factor authentication. Set up a method now — you will be asked for it the next time you sign in.'
              : 'A second sign-in code is required on this console. Add a way to get one to keep signing in.'}
          </Alert>
        ) : null}

        {recoveryCodes ? (
          <div className="rounded border border-amber-300 bg-amber-50 p-4">
            <h2 className="font-semibold">Save your recovery codes</h2>
            <p className="text-sm">
              These are shown once. Each one gets you in if you lose your other methods, and each works only once.
            </p>
            <ul className="my-3 grid grid-cols-2 gap-1 font-mono text-sm">
              {recoveryCodes.map((recoveryCode) => (
                <li key={recoveryCode}>{recoveryCode}</li>
              ))}
            </ul>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void navigator.clipboard?.writeText(recoveryCodes.join('\n'))}
            >
              Copy codes
            </Button>{' '}
            <Button type="button" variant="ghost" onClick={() => setRecoveryCodes(null)}>
              Done
            </Button>
          </div>
        ) : null}

        <WorkSection title="Your methods">
          {data.methods.length === 0 ? (
            <p className="mt-1 text-sm text-[color:var(--color-muted)]">You have not set up a second factor yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-[color:var(--color-line)]">
              {data.methods.map((method) => (
                <li key={method.id} className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium">{method.label}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removeMethod.mutate(method.id)}
                    disabled={removeMethod.isPending}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {enrollment ? (
            <div className="mt-4 space-y-3 rounded bg-[color:var(--color-surface)] p-3">
              <p className="text-sm">
                Open your authenticator app and add a new account using the address below, or type the setup key by
                hand.
              </p>
              {/* The URI and the key in plain text: a QR code is unusable to
                somebody enrolling on the same screen that displays it, and
                unreadable to somebody using a screen reader. */}
              <p className="break-all font-mono text-xs">{enrollment.uri}</p>
              <p className="text-sm">
                Setup key: <span className="font-mono">{enrollment.secret}</span>
              </p>
              <TextInput
                id="admin-totp-code"
                label="Enter the 6-digit code from your app"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
              <Button type="button" onClick={() => finishTotp.mutate()} disabled={finishTotp.isPending}>
                Confirm
              </Button>{' '}
              <Button type="button" variant="ghost" onClick={() => setEnrollment(null)}>
                Cancel
              </Button>
            </div>
          ) : smsChallenge ? (
            <div className="mt-4 space-y-3 rounded bg-[color:var(--color-surface)] p-3">
              <p className="text-sm">We sent a code to {smsChallenge.maskedPhone}.</p>
              <TextInput
                id="admin-sms-code"
                label="Enter the code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
              <Button type="button" onClick={() => finishSms.mutate()} disabled={finishSms.isPending}>
                Confirm
              </Button>{' '}
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSmsChallenge(null);
                  setCode('');
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {data.availableMethods.includes('totp') ? (
                <Button type="button" onClick={() => startTotp.mutate()} disabled={startTotp.isPending}>
                  Add an authenticator app
                </Button>
              ) : null}
              {data.availableMethods.includes('webauthn') && webauthnSupported() ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => addSecurityKey.mutate()}
                  disabled={addSecurityKey.isPending}
                >
                  Add a security key
                </Button>
              ) : null}
            </div>
          )}

          {/* Only where `mfa_admin_allow_sms` is on. Off by default, because an
            operator who can execute a payout batch should not be one SIM swap
            away from an attacker — but a deployment that turns it on has to be
            able to enroll one, or the setting means nothing. */}
          {!enrollment && !smsChallenge && data.availableMethods.includes('sms') ? (
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <TextInput
                id="admin-sms-phone"
                label="Mobile number (international format)"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                inputMode="tel"
                autoComplete="tel"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => startSms.mutate()}
                disabled={startSms.isPending || phone.trim().length === 0}
              >
                Send a code
              </Button>
            </div>
          ) : null}
        </WorkSection>

        {/* Losing a remembered laptop, or spotting a console session you do not
          recognize, has to be answerable from here — signing out ends only
          this session, and a remembered browser is accepted until it expires. */}
        <WorkSection title="Remembered browsers">
          <p className="text-sm text-[color:var(--color-muted)]">
            A remembered browser skips the code at sign-in for {data.trustedDeviceDays} days. It never replaces the
            check in front of a payout.
          </p>
          {devices.isError ? (
            <Alert tone="error">
              Remembered browsers could not be loaded.{' '}
              <Button variant="ghost" onClick={() => void devices.refetch()}>
                Try again
              </Button>
            </Alert>
          ) : null}
          <ul className="mt-2 text-sm">
            {(devices.data?.devices ?? []).map((device) => (
              <li key={device.id} className="py-1">
                {device.current ? 'This browser' : 'Another browser'} · last used{' '}
                {new Date(device.lastUsedAt).toLocaleDateString()}
              </li>
            ))}
          </ul>
          <Button type="button" variant="secondary" className="mt-2" onClick={() => clearDevices.mutate()}>
            Forget all browsers
          </Button>
        </WorkSection>

        <WorkSection title="Where you are signed in">
          {sessions.isError ? (
            <Alert tone="error">
              Sessions could not be loaded.{' '}
              <Button variant="ghost" onClick={() => void sessions.refetch()}>
                Try again
              </Button>
            </Alert>
          ) : null}
          <ul className="mt-2 text-sm">
            {(sessions.data?.sessions ?? []).map((session) => (
              <li key={session.id} className="py-1">
                {session.current ? 'This browser' : 'Another browser'} · {new Date(session.lastUsedAt).toLocaleString()}
              </li>
            ))}
          </ul>
          <Button type="button" variant="secondary" className="mt-2" onClick={() => signOutOthers.mutate()}>
            Sign out everywhere else
          </Button>
        </WorkSection>

        <WorkSection title="Recovery codes">
          <p className="text-sm text-[color:var(--color-muted)]">
            {data.recoveryCodesRemaining} unused {data.recoveryCodesRemaining === 1 ? 'code' : 'codes'} remaining.
          </p>
          <Button
            type="button"
            variant="secondary"
            className="mt-2"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending || data.methods.length === 0}
          >
            Generate new codes
          </Button>
        </WorkSection>
      </WorkPage>
    </div>
  );
}
